import type { Http } from "../http";
import { ApiError } from "../errors";
import { isRecord } from "../http";
import { geoPlaceSchema, type BirthData, type GeoPlace } from "../models/birth";
import {
  chartEnvelopeSchema,
  chartMenuSchema,
  chartSchema,
  userProfileSchema,
  vargaChartSchema,
  type Chart,
  type ChartSummary,
  type UserProfile,
  type VargaChart,
} from "../models/chart";
import { lenientArray, parseBody } from "../models/common";
import { weekSchema, type WeekReading } from "../models/week";

/** The week is written by a model; give it longer than an ordinary call. */
const WEEK_TIMEOUT_MS = 45_000;

/** Birth details, charts and the person's own profile. */
export function profileEndpoints(http: Http) {
  return {
    async geocode(query: string, limit = 8): Promise<readonly GeoPlace[]> {
      const body = await http.get("/geocode", { auth: false, query: { q: query, limit } });
      return parseBody(lenientArray(geoPlaceSchema), body);
    },

    /**
     * A chart for birth details nobody has signed in for yet. Backs the
     * first-run reveal; nothing is persisted.
     */
    async previewChart(birth: BirthData): Promise<Chart> {
      const body = await http.post("/chart/preview", { birth }, { auth: false });
      const chart = isRecord(body) ? body["chart"] : undefined;
      if (!isRecord(chart)) throw ApiError.badResponse("bad_preview_response");
      return parseBody(chartSchema, chart);
    },

    /**
     * This week for these birth details, before there is an account: four
     * areas with a do and a don't each, plus the best and careful days.
     */
    async previewWeek(birth: BirthData): Promise<WeekReading> {
      const body = await http.post("/chart/week-preview", { birth }, { auth: false, timeoutMs: WEEK_TIMEOUT_MS });
      return parseBody(weekSchema, body);
    },

    async createBirthProfile(birth: BirthData): Promise<UserProfile> {
      const body = await http.post("/birth-profiles", { relation: "self", birth });
      if (!isRecord(body) || !isRecord(body["chart"])) {
        throw ApiError.badResponse("bad_profile_response");
      }
      const envelope = parseBody(chartEnvelopeSchema, body);
      // The response carries no birth echo; attach what we sent.
      return { profile_id: envelope.profile_id, birth, chart: envelope.chart };
    },

    /** The signed-in person's profile, or null when none exists yet. */
    async fetchMyProfile(): Promise<UserProfile | null> {
      try {
        return parseBody(userProfileSchema, await http.get("/me/profile"));
      } catch (error) {
        if (error instanceof ApiError && error.isNotFound) return null;
        throw error;
      }
    },

    async fetchVarga(divisor: number): Promise<VargaChart> {
      return parseBody(vargaChartSchema, await http.get("/me/varga", { query: { divisor } }));
    },

    async fetchChartMenu(): Promise<readonly ChartSummary[]> {
      return parseBody(chartMenuSchema, await http.get("/me/charts")).charts;
    },
  };
}
